import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
	LineChart,
	Line,
	Legend,
} from 'recharts';
import userStore from '../../../zustand/store/userStore';
import { getStudentDashboardAPI } from '../../../zustand/API';
import {
	Trophy,
	TrendingUp,
	TrendingDown,
	User,
	Star,
	Calendar as CalendarIcon,
} from 'lucide-react';
import Calender from '../../../../src/components/Calender/Calender';
import AppHeader from '../../../components/Dashboard/AppHeader';
import { useNavigate } from 'react-router';
import studentbar from '../../../assets/icons/studentbar.svg';
import { format } from 'date-fns';
import { Content } from 'antd/es/layout/layout';
import { message as antdMessage } from 'antd';

const fadeIn = {
	hidden: { opacity: 0, y: 40 },
	show: { opacity: 1, y: 0 },
};

const SummaryCard = ({ label, value, icon }) => (
	<motion.div
		variants={fadeIn}
		className="rounded-2xl backdrop-blur-md bg-white/40 border border-white/30 shadow-xl p-5 transition-all duration-300 hover:scale-[1.02]"
	>
		<div className="flex items-center justify-between">
			<div>
				<p className="text-xs uppercase tracking-wider text-gray-500">
					{label}
				</p>
				<h3 className="text-2xl font-bold text-gray-800">{value}</h3>
			</div>
			<div className="text-indigo-600">{icon}</div>
		</div>
	</motion.div>
);
const LeaderboardCard = ({ email, score, rank }) => {
	const rankColors = ['text-yellow-500', 'text-gray-400', 'text-yellow-800'];

	return (
		<motion.div
			variants={fadeIn}
			className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 p-4 rounded-xl bg-white/70 backdrop-blur-md border shadow-lg transition-all ${rank === 1 ? 'ring-2 ring-yellow-400' : ''
				}`}
		>
			<div className="flex items-center gap-2 sm:gap-3">
				{rank <= 3 && <Star className={`w-5 h-5 ${rankColors[rank - 1]}`} />}
				<span className="font-medium text-gray-800 text-sm sm:text-base break-all">
					{email}
				</span>
			</div>
			<div className="text-indigo-700 font-bold text-sm sm:text-base">
				{score}
			</div>
		</motion.div>
	);
};

const badgeStyle = (type) => {
	const base = 'px-3 py-1 rounded-full text-xs font-semibold tracking-wide';
	const styles = {
		best: `${base} bg-green-100 text-green-800`,
		average: `${base} bg-yellow-100 text-yellow-800`,
		weak: `${base} bg-red-100 text-red-800`,
	};
	return styles[type] || base;
};

const StudentDashboard = () => {
	const { user } = userStore();
	const [dashboard, setDashboard] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				const response = await getStudentDashboardAPI({ email: user?.email });
				if (response?.data && Object.keys(response.data).length > 0) {
					setDashboard(response.data);
				} else {
					setError(true);
				}
			} catch (err) {
				console.error('Error fetching student dashboard:', err);
				setError(true);
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, []);

	if (loading) {
		return (
			<>
				<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-100 to-white">
					<div className="text-center space-y-4">
						<div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
						<p className="text-gray-600 font-medium">Loading your dashboard...</p>
					</div>
				</div>
			</>
		);
	}

	if (error || !dashboard || Object.keys(dashboard).length === 0) {
		return (
			<div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-indigo-100 to-white text-gray-700 p-10">
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8 }}
					className="text-center space-y-6"
				>
					<Trophy className="w-16 h-16 mx-auto text-indigo-500" />
					<h1 className="text-3xl font-bold">No Performance Data Yet</h1>
					<p className="text-gray-500 max-w-md mx-auto">
						You haven’t attempted any quizzes yet. Once you start participating,
						your performance stats and leaderboard rank will show up here.
					</p>
					<button
						onClick={() => navigate('/view-course')}
						className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition"
					>
						🚀 Start Learning
					</button>
				</motion.div>
			</div>
		);
	}

	const summaryCards = [
		{
			label: 'Total Quizzes',
			value: dashboard.summary.totalQuizzes,
			icon: <User className="w-6 h-6" />,
		},
		{
			label: 'Average Score',
			value: `${dashboard.summary.averageScore}%`,
			icon: <TrendingUp className="w-6 h-6" />,
		},
		{
			label: 'Highest Score',
			value: dashboard.summary.highestScore,
			icon: <TrendingUp className="w-6 h-6" />,
		},
		{
			label: 'Lowest Score',
			value: dashboard.summary.lowestScore,
			icon: <TrendingDown className="w-6 h-6" />,
		},
		{
			label: 'Accuracy Rate',
			value: dashboard.summary.accuracyRate,
			icon: <TrendingUp className="w-6 h-6" />,
		},
	];

	return (
		<Content className='p-2'>
			<Content>
				<AppHeader title={`Welcome, ${user?.fullName} 👋`} />
			</Content>
			<div className="p-6 space-y-10 bg-gradient-to-br from-indigo-50 to-white min-h-screen">
				<div className="relative bg-teal-500 text-white p-4 rounded-xl flex justify-between mt-4">
					<div className="font-[600] flex items-center lg:text-[24px]/[2] md:text-[18px]/[2] sm:text-[12px]/8">
						The central hub for navigation <br /> and accessing key features
					</div>
					<img src={studentbar} alt="" className="mr-4" />
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
					{summaryCards.map((card, i) => (
						<SummaryCard key={i} {...card} />
					))}
				</div>

				<motion.div variants={fadeIn} initial="hidden" animate="show">
					<h2 className="text-xl font-semibold text-gray-800 mb-2">
						📈 Subject Performance
					</h2>
					<div className="flex flex-wrap gap-3">
						{['best', 'average', 'weak'].map((type) =>
							dashboard.subjectPerformance[type]?.map((subj, i) => (
								<span key={i} className={badgeStyle(type)}>
									{type.toUpperCase()}: {subj}
								</span>
							)),
						)}
					</div>
				</motion.div>

				<motion.div
					variants={fadeIn}
					initial="hidden"
					animate="show"
					className="bg-white/60 backdrop-blur-lg rounded-2xl p-6 shadow-xl"
				>
					<h2 className="text-lg font-semibold text-gray-800 mb-4">
						📊 Performance by Subject
					</h2>
					<ResponsiveContainer width="100%" height={300}>
						<BarChart data={dashboard.performanceBySubject}>
							<defs>
								<linearGradient id="barColor" x1="0" y1="0" x2="0" y2="1">
									<stop offset="0%" stopColor="#4f46e5" stopOpacity={0.8} />
									<stop offset="100%" stopColor="#c7d2fe" stopOpacity={0.3} />
								</linearGradient>
							</defs>
							<XAxis dataKey="subject" />
							<YAxis />
							<Tooltip />
							<Bar
								dataKey="average"
								fill="url(#barColor)"
								radius={[10, 10, 0, 0]}
							/>
						</BarChart>
					</ResponsiveContainer>
				</motion.div>

				<motion.div
					variants={fadeIn}
					initial="hidden"
					animate="show"
					className="bg-white/60 backdrop-blur-lg rounded-2xl p-6 shadow-xl"
				>
					<h2 className="text-lg font-semibold text-gray-800 mb-4">
						📉 Performance Trend
					</h2>
					<ResponsiveContainer width="100%" height={300}>
						<LineChart data={dashboard.performanceTrend}>
							<XAxis
								dataKey="date"
								tickFormatter={(tick) =>
									format(new Date(tick), 'MMM d, yyyy - h:mm a')
								}
							/>
							<YAxis />
							<Tooltip />
							<Legend />
							<Line
								type="monotone"
								dataKey="score"
								stroke="#14b8a6"
								strokeWidth={3}
								dot={{ r: 4 }}
							/>
						</LineChart>
					</ResponsiveContainer>
				</motion.div>

				<motion.div
					variants={fadeIn}
					initial="hidden"
					animate="show"
					className="bg-white/60 backdrop-blur-lg rounded-2xl p-6 shadow-xl"
				>
					<h2 className="text-lg font-semibold text-gray-800 mb-2">
						🏆 Leaderboard
					</h2>
					<p className="text-sm text-gray-500 mb-4">
						Your Current Rank: <strong>#{dashboard.leaderboard.rank}</strong>
					</p>
					<div className="space-y-3">
						{dashboard.leaderboard.topStudents.map((student, i) => (
							<LeaderboardCard
								key={i}
								email={student.email}
								score={student.score}
								rank={i + 1}
							/>
						))}
					</div>
				</motion.div>

				<motion.div
					variants={fadeIn}
					initial="hidden"
					animate="show"
					className="bg-white/60 backdrop-blur-lg rounded-2xl p-6 shadow-xl"
				>
					<div className="flex items-center gap-2 mb-4">
						<CalendarIcon className="text-indigo-600" />
						<h2 className="text-lg font-semibold text-gray-800"> Calendar</h2>
					</div>
					<Calender />
				</motion.div>
			</div>
		</Content>
	);
};

export default StudentDashboard;
